import React from 'react';
import { newIdempotencyKey } from 'services/businessBilling/idempotency';
import { policyApi } from 'services/billingPolicy/api';
import { Attempt, attemptStore, SavedAttempts } from 'services/billingPolicy/attempts';
import { BillingOriginCheckRequest, BillingOriginCheckResponse, BillingOriginPublicSession, BillingOriginSettlement, BillingOriginSettlementRequest } from 'services/billingPolicy/contracts/BillingOrigin';
import { BillingPolicyAnswerField, BillingPolicyAnswers } from 'services/billingPolicy/contracts/BillingPolicy';
import { checkIdentity, DocumentCase, initialAnswers, invalidAnswerFields, parseCheckResult, parsePolicyError, parsePolicySession } from 'services/billingPolicy/validation';

type Pending = 'check' | 'settlement' | 'cancel' | null;
type Phase = 'idle' | 'check' | 'settlement' | 'cancel' | 'reconcile';
type Notice = 'invalid' | 'check-failed' | 'check-unavailable' | 'unknown' | 'storage' | 'unauthorized' | 'expired' | null;
interface BoundCheck { result: BillingOriginCheckResponse; identity: string }

const restoredAnswers = (selected: DocumentCase, result?: BillingOriginCheckResponse): BillingPolicyAnswers => ({
    ...initialAnswers(selected),
    ...result?.inputs,
});

export const usePolicyBilling = (initial: BillingOriginPublicSession, tenantId: string, verificationToken: string) => {
    const storage = React.useMemo(() => attemptStore(tenantId, initial.token), [tenantId, initial.token]);
    const [restored] = React.useState(() => {
        try {
            const saved = storage.read();
            if (Object.values(saved).some((attempt) => attempt.policyRevision !== initial.descriptor.revision)) throw new Error('ATTEMPT_REVISION_CONFLICT');
            return { saved, failed: false };
        } catch { return { saved: {} as SavedAttempts, failed: true }; }
    });
    const checkAtLoad = initial.checkResult;
    const initialCase = initial.descriptor.documentCases.find((item) => item.id === restored.saved.check?.documentCaseId);
    const recoverableCheck = checkAtLoad && checkAtLoad.attemptId === restored.saved.check?.attemptId
        && checkAtLoad.documentCaseId === restored.saved.check.documentCaseId && checkAtLoad.policyRevision === initial.descriptor.revision;
    const [session, setSession] = React.useState(initial);
    const [caseId, setCaseId] = React.useState(initialCase?.id || '');
    const [answers, setAnswers] = React.useState<BillingPolicyAnswers>(() => initialCase ? restoredAnswers(initialCase, recoverableCheck ? checkAtLoad : undefined) : {});
    const [checked, setChecked] = React.useState<BoundCheck | null>(() => initialCase && recoverableCheck && checkAtLoad.status !== 'pending'
        ? { result: checkAtLoad, identity: checkIdentity(initialCase, restoredAnswers(initialCase, checkAtLoad)) } : null);
    // The initial session is authenticated. A check cannot accept details, so its lost body must not lock an OPEN form.
    const [pending, setPending] = React.useState<Pending>(() => initial.status !== 'OPEN' ? null
        : restored.saved.cancel ? 'cancel' : restored.saved.settlement ? 'settlement' : null);
    const pendingRef = React.useRef(pending);
    const [phase, setPhase] = React.useState<Phase>('idle');
    const [notice, setNotice] = React.useState<Notice>(restored.failed ? 'storage'
        : recoverableCheck && checkAtLoad.status === 'failed' ? 'check-failed'
            : recoverableCheck && checkAtLoad.status === 'unavailable' ? 'check-unavailable'
                : restored.saved.check && (!recoverableCheck || checkAtLoad?.status === 'pending') ? 'unknown' : null);
    const [invalidFields, setInvalidFields] = React.useState<string[]>([]);
    const [touchedFields, setTouchedFields] = React.useState<string[]>([]);
    const [now, setNow] = React.useState(Date.now());
    const generation = React.useRef(0);
    const financialLock = React.useRef(false);
    const checkLock = React.useRef(false);
    const mounted = React.useRef(true);
    const checkAttempt = React.useRef<Attempt | undefined>(restored.saved.check);
    const checkRequest = React.useRef<{ request: BillingOriginCheckRequest; identity: string; retry: boolean }>();
    const settlementRequest = React.useRef<BillingOriginSettlementRequest>();
    const settlementAttempt = React.useRef<Attempt | undefined>(restored.saved.settlement);
    const editing = React.useRef<{ field: BillingPolicyAnswerField; initialValue: string | boolean | undefined }>();
    const selected = session.descriptor.documentCases.find((item) => item.id === caseId);
    const currentIdentity = selected ? checkIdentity(selected, answers) : '';
    const relevantCheck = checked?.identity === currentIdentity && checked.result.documentCaseId === caseId ? checked.result : null;
    const verificationDeadline = relevantCheck?.verificationExpiresAt ? Date.parse(relevantCheck.verificationExpiresAt) : Infinity;
    const deadline = Math.min(Date.parse(session.browserAccessExpiresAt), verificationDeadline > now ? verificationDeadline : Infinity);
    React.useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    React.useEffect(() => {
        if (deadline <= Date.now()) { setNow(Date.now()); return; }
        const timer = window.setTimeout(() => setNow(Date.now()), Math.min(deadline - Date.now() + 1, 2_147_483_647));
        return () => window.clearTimeout(timer);
    }, [deadline]);

    const markPending = (value: Pending) => { pendingRef.current = value; setPending(value); };
    const allowed = (action: typeof session.allowedActions[number]) => session.status === 'OPEN' && session.allowedActions.includes(action);
    const accessExpired = now >= Date.parse(session.browserAccessExpiresAt);
    const open = session.status === 'OPEN' && !accessExpired && !['storage', 'unauthorized', 'expired'].includes(notice || '');
    const busy = ['settlement', 'cancel', 'reconcile'].includes(phase);
    const canEdit = open && !pending && !busy && allowed('EDIT_DETAILS');
    const checksPassed = Boolean(selected && (selected.checks.length === 0 || (relevantCheck?.status === 'passed'
        && (!relevantCheck.verificationExpiresAt || Date.parse(relevantCheck.verificationExpiresAt) > now))));
    const answerErrors = selected ? invalidAnswerFields(selected, answers) : [];
    const visibleInvalidFields = Array.from(new Set([...invalidFields, ...answerErrors.filter(field => touchedFields.includes(field))]));
    const formValid = Boolean(selected && answerErrors.length === 0);
    const canSubmit = open && !pending && phase === 'idle' && formValid && checksPassed && Boolean(selected)
        && (selected?.kind === 'NO_DETAILS' ? allowed('CONTINUE_WITHOUT_DETAILS') : allowed('EDIT_DETAILS'));
    const awaitingTerminalConfirmation = session.status === 'ACCEPTED' && session.terminalConfirmation !== 'ACCEPTED';
    // Authenticated acceptance identifies the winner when another tab overwrote browser correlation.
    const confirmationAttempt = session.accepted?.attemptId ? { attemptId: session.accepted.attemptId,
        policyRevision: session.descriptor.revision, documentCaseId: session.accepted.documentCaseId } : settlementAttempt.current;
    const canRetryTerminalConfirmation = awaitingTerminalConfirmation && phase === 'idle'
        && !['unauthorized', 'expired'].includes(notice || '') && Boolean(confirmationAttempt)
        && confirmationAttempt?.policyRevision === session.descriptor.revision
        && confirmationAttempt?.documentCaseId === session.accepted?.documentCaseId;

    // These documented errors are returned before any settlement write. Unknown outcomes stay locked.
    const handleRejected = (status: number, body: unknown, kind: 'check' | 'settlement', attemptId: string): boolean => {
        const error = parsePolicyError(body);
        if (!error) return false;
        if ((status === 401 && error.code === 'BILLING_SESSION_UNAUTHORIZED') || (status === 410 && error.code === 'BILLING_SESSION_EXPIRED')) {
            setNotice(status === 401 ? 'unauthorized' : 'expired');
            return true;
        }
        if (status !== 400 || !['BILLING_DETAILS_INVALID', 'BILLING_CHECK_REQUIRED', 'BILLING_VERIFICATION_STALE',
            'BILLING_DOCUMENT_INELIGIBLE', 'BILLING_DOCUMENT_UNSUPPORTED', 'BILLING_DEFAULT_NOT_PERMITTED', 'BILLING_REQUEST_INVALID'].includes(error.code)) return false;
        try { storage.remove(kind, attemptId); } catch { setNotice('storage'); return true; }
        if (error.code === 'BILLING_CHECK_REQUIRED' || error.code === 'BILLING_VERIFICATION_STALE') setChecked(null);
        if (kind === 'settlement') settlementRequest.current = undefined;
        else { checkAttempt.current = undefined; checkRequest.current = undefined; }
        markPending(null);
        setInvalidFields((error.fields || []).map((field) => field.path.split('/').pop() || '').filter(Boolean));
        setNotice(error.code === 'BILLING_CHECK_REQUIRED' || error.code === 'BILLING_VERIFICATION_STALE' ? 'check-failed' : 'invalid');
        return true;
    };

    const applySession = (body: unknown): BillingOriginPublicSession | null => {
        const next = parsePolicySession(body, initial.token);
        if (!next || next.descriptor.revision !== initial.descriptor.revision
            || (session.status === 'ACCEPTED' && (next.status !== 'ACCEPTED'
                || next.accepted?.revision !== session.accepted?.revision
                || next.accepted?.documentCaseId !== session.accepted?.documentCaseId))) { setNotice('unknown'); return null; }
        setSession(next);
        setNow(Date.now());
        if (next.status !== 'OPEN') {
            markPending(null);
            setNotice(null);
            settlementRequest.current = undefined;
            if (next.status !== 'ACCEPTED' || next.terminalConfirmation === 'ACCEPTED') {
                settlementAttempt.current = undefined;
                try { storage.clear(); } catch { /* A terminal server outcome remains authoritative. */ }
            }
        }
        return next;
    };

    const reconcileResponse = async (): Promise<BillingOriginPublicSession | null> => {
        try {
            const body = await policyApi.load(tenantId, initial.token, verificationToken);
            if (!mounted.current) return null;
            const next = applySession(body);
            if (next?.status === 'OPEN') {
                const saved = storage.read();
                // A check result cannot resolve an uncertain acceptance or cancellation, including another tab's attempt.
                if (pendingRef.current === 'settlement' || pendingRef.current === 'cancel') return next;
                if (saved.settlement || saved.cancel) {
                    markPending(saved.cancel ? 'cancel' : 'settlement'); setNotice('unknown'); return next;
                }
                // Authenticated OPEN resolves check-only uncertainty; retain its body for an exact retry.
                if (pendingRef.current === 'check') markPending(null);
                const expected = checkAttempt.current;
                const result = next.checkResult;
                const target = next.descriptor.documentCases.find((item) => item.id === result?.documentCaseId);
                if (result && target && result.attemptId === expected?.attemptId && result.documentCaseId === expected.documentCaseId
                    && result.policyRevision === expected.policyRevision && target.id === caseId && result.status !== 'pending') {
                    const retained = checkRequest.current;
                    const identity = checkIdentity(target, result.inputs);
                    if (retained && (retained.request.attemptId !== result.attemptId || identity !== retained.identity
                        || currentIdentity !== retained.identity)) { setNotice('unknown'); return next; }
                    // A live request's contact fields may have changed or been cleared while verification ran.
                    // After reload only the correlated server check inputs can be restored, preserving any new contact edits.
                    if (!retained) setAnswers((current) => ({ ...current, ...result.inputs }));
                    else retained.retry = false;
                    setChecked({ result, identity });
                    markPending(null);
                    setNotice(result.status === 'passed' ? null : result.status === 'unavailable' ? 'check-unavailable' : 'check-failed');
                }
            }
            return next;
        } catch (error) {
            if (mounted.current) {
                const response = (error as { response?: { status?: number; data?: unknown } })?.response;
                const rejected = parsePolicyError(response?.data);
                setNotice(response?.status === 401 && rejected?.code === 'BILLING_SESSION_UNAUTHORIZED' ? 'unauthorized'
                    : response?.status === 410 && rejected?.code === 'BILLING_SESSION_EXPIRED' ? 'expired' : 'unknown');
            }
            return null;
        }
    };

    const reconcile = async () => {
        if (financialLock.current || checkLock.current) return;
        financialLock.current = true;
        setPhase('reconcile');
        await reconcileResponse();
        financialLock.current = false;
        if (mounted.current) setPhase('idle');
    };

    const invalidate = () => {
        generation.current++;
        checkLock.current = false;
        checkAttempt.current = undefined;
        checkRequest.current = undefined;
        setChecked(null);
        setNotice(null);
        setInvalidFields([]);
        setPhase('idle');
    };
    const choose = (id: string) => {
        if (!open || pendingRef.current || financialLock.current || !allowed('CHOOSE_DOCUMENT')) return;
        const next = session.descriptor.documentCases.find((item) => item.id === id);
        if (!next) return;
        invalidate();
        editing.current = undefined;
        setTouchedFields([]);
        setCaseId(id);
        setAnswers(initialAnswers(next));
    };
    const beginEdit = (field: BillingPolicyAnswerField) => { editing.current = { field, initialValue: answers[field] }; };
    const endEdit = (field: BillingPolicyAnswerField) => {
        if (editing.current?.field === field) editing.current = undefined;
        setTouchedFields(current => current.includes(field) ? current : [...current, field]);
    };
    const edit = (field: BillingPolicyAnswerField, value: string | boolean) => {
        if (!canEdit || pendingRef.current || financialLock.current || !selected || selected.inputSchema.properties[field]?.const !== undefined) return;
        const next = { ...answers };
        if (value === '') delete next[field]; else next[field] = value;
        // Keep the focus-start value throughout typing; the first letter is not an existing complete parent.
        const previous = editing.current?.field === field ? editing.current.initialValue : answers[field];
        if (previous !== undefined && previous !== '' && previous !== value) {
            const dependents: Partial<Record<BillingPolicyAnswerField, BillingPolicyAnswerField[]>> = {
                addressCountryCode: ['addressRegion', 'addressCity', 'addressLine1', 'addressLine2', 'addressPostalCode'],
                addressRegion: ['addressCity', 'addressLine1', 'addressLine2', 'addressPostalCode'],
                addressCity: ['addressLine1', 'addressLine2', 'addressPostalCode'],
            };
            for (const child of dependents[field] || []) {
                if (selected.inputSchema.properties[child]?.const === undefined) delete next[child];
            }
        }
        if (checkIdentity(selected, next) !== currentIdentity) invalidate();
        else {
            setNotice((current) => current === 'unknown' && checkAttempt.current ? current : null);
            setInvalidFields((current) => current.filter((name) => name !== field));
        }
        setAnswers(next);
    };

    const editAddress = (values: BillingPolicyAnswers) => {
        if (!canEdit || pendingRef.current || financialLock.current || !selected) return;
        setAnswers(current => {
            const next = { ...current };
            for (const [field, value] of Object.entries(values)) {
                const key = field as BillingPolicyAnswerField;
                const schema = selected.inputSchema.properties[key];
                if (!field.startsWith('address') || !schema || schema.const !== undefined) continue;
                if (value === '') delete next[key]; else next[key] = value;
            }
            return next;
        });
        setInvalidFields(current => current.filter(field => !field.startsWith('address')));
        setNotice(null);
    };

    const check = async () => {
        if (!open || financialLock.current || checkLock.current || !selected || !allowed('RETRY_CHECK')
            || (pendingRef.current && (pendingRef.current !== 'check' || !checkRequest.current))) return;
        const invalid = invalidAnswerFields(selected, answers);
        if (invalid.length) { setInvalidFields(invalid); setNotice('invalid'); return; }
        const retained = checkRequest.current?.retry ? checkRequest.current : undefined;
        const request = retained?.request || { verificationToken, attemptId: newIdempotencyKey(), policyRevision: session.descriptor.revision, documentCaseId: selected.id, answers: { ...answers } };
        try {
            const saved = storage.read();
            if (saved.settlement || saved.cancel) { markPending(saved.cancel ? 'cancel' : 'settlement'); setNotice('unknown'); return; }
            storage.save('check', request);
        } catch { setNotice('storage'); return; }
        const identity = retained?.identity || currentIdentity;
        checkAttempt.current = { attemptId: request.attemptId, policyRevision: request.policyRevision, documentCaseId: request.documentCaseId };
        checkRequest.current = { request, identity, retry: false };
        const requestGeneration = ++generation.current;
        checkLock.current = true;
        setPhase('check');
        setNotice(null);
        setChecked(null);
        try {
            const response = await policyApi.check(tenantId, initial.token, request);
            if (!mounted.current || generation.current !== requestGeneration) return;
            if (handleRejected(response.status, response.body, 'check', request.attemptId)) return;
            if (response.status === 409 && parsePolicyError(response.body)?.code === 'BILLING_CHECK_PENDING') {
                // The server still owns the lease. Keep an exact retry available without locking unrelated form actions.
                checkRequest.current!.retry = true; markPending(null); setNotice('unknown'); return;
            }
            const result = response.status === 200 ? parseCheckResult(response.body, session.descriptor) : null;
            if (!result || result.attemptId !== request.attemptId || result.documentCaseId !== request.documentCaseId
                || checkIdentity(selected, result.inputs) !== identity) {
                checkRequest.current!.retry = true; markPending('check'); setNotice('unknown');
            } else if (result.status === 'pending') {
                checkRequest.current!.retry = true; markPending(null); setNotice('unknown');
            } else {
                setChecked({ result, identity }); markPending(null);
                setNotice(result.status === 'passed' ? null : result.status === 'unavailable' ? 'check-unavailable' : 'check-failed');
            }
        } catch {
            if (mounted.current && generation.current === requestGeneration) {
                checkRequest.current!.retry = true; markPending('check'); setNotice('unknown');
            }
        } finally {
            if (mounted.current && generation.current === requestGeneration) { checkLock.current = false; setPhase('idle'); }
        }
    };

    const dispatchSettlement = async (request: BillingOriginSettlement) => {
        if (financialLock.current || checkLock.current) return;
        const confirmationOnly = 'acceptedRevision' in request;
        if (!confirmationOnly) {
            try { storage.save('settlement', request); } catch { setNotice('storage'); return; }
            settlementRequest.current = request;
            settlementAttempt.current = { attemptId: request.attemptId, policyRevision: request.policyRevision, documentCaseId: request.documentCaseId };
        }
        financialLock.current = true;
        generation.current++;
        markPending('settlement');
        setPhase('settlement');
        setNotice(null);
        try {
            const response = await policyApi.settle(tenantId, initial.token, request);
            if (!mounted.current) return;
            if (!confirmationOnly && handleRejected(response.status, response.body, 'settlement', request.attemptId)) return;
            if (confirmationOnly && response.status === 401 && parsePolicyError(response.body)?.code === 'BILLING_SESSION_UNAUTHORIZED') {
                setNotice('unauthorized'); return;
            }
            const next = response.status === 200 ? applySession(response.body) : null;
            if (!next || next.status === 'OPEN') { setNotice('unknown'); await reconcileResponse(); }
        } catch {
            if (mounted.current) { setNotice('unknown'); await reconcileResponse(); }
        } finally {
            financialLock.current = false;
            if (mounted.current) setPhase('idle');
        }
    };

    const settle = async (withoutDetails = false) => {
        if (!open || Date.parse(session.browserAccessExpiresAt) <= Date.now() || pendingRef.current || financialLock.current || checkLock.current) return;
        const target = withoutDetails ? session.descriptor.documentCases.find((item) => item.id === session.descriptor.defaults.noDetailsCaseId) : selected;
        if (!target || (target.kind === 'NO_DETAILS' ? !allowed('CONTINUE_WITHOUT_DETAILS') : !canSubmit)) return;
        if (!withoutDetails && relevantCheck?.verificationExpiresAt && Date.parse(relevantCheck.verificationExpiresAt) <= Date.now()) { setNow(Date.now()); return; }
        try {
            const saved = storage.read();
            if (saved.settlement || saved.cancel) { markPending(saved.cancel ? 'cancel' : 'settlement'); setNotice('unknown'); return; }
        } catch { setNotice('storage'); return; }
        await dispatchSettlement({ verificationToken, attemptId: newIdempotencyKey(), policyRevision: session.descriptor.revision,
            documentCaseId: target.id, answers: withoutDetails || target.kind === 'NO_DETAILS' ? {} : { ...answers },
            ...(!withoutDetails && relevantCheck?.verificationReference ? { verificationReference: relevantCheck.verificationReference } : {}),
        });
    };

    const retrySettlement = async () => {
        if (canRetryTerminalConfirmation && session.accepted && confirmationAttempt?.documentCaseId) {
            await dispatchSettlement({ verificationToken, attemptId: confirmationAttempt.attemptId,
                policyRevision: confirmationAttempt.policyRevision, documentCaseId: confirmationAttempt.documentCaseId,
                acceptedRevision: session.accepted.revision });
        } else if (open && pendingRef.current === 'settlement' && settlementRequest.current) await dispatchSettlement(settlementRequest.current);
    };

    const cancel = async () => {
        if (!open || !allowed('CANCEL') || financialLock.current || checkLock.current) return;
        let attemptId: string;
        try {
            attemptId = storage.read().cancel?.attemptId || newIdempotencyKey();
            storage.save('cancel', { attemptId, policyRevision: session.descriptor.revision });
        } catch { setNotice('storage'); return; }
        financialLock.current = true;
        generation.current++;
        markPending('cancel');
        setPhase('cancel');
        setNotice(null);
        try {
            const response = await policyApi.cancel(tenantId, initial.token, { verificationToken, attemptId, policyRevision: session.descriptor.revision });
            if (!mounted.current) return;
            const next = response.status === 200 ? applySession(response.body) : null;
            if (!next || next.status === 'OPEN') { setNotice('unknown'); await reconcileResponse(); }
        } catch {
            if (mounted.current) { setNotice('unknown'); await reconcileResponse(); }
        } finally {
            financialLock.current = false;
            if (mounted.current) setPhase('idle');
        }
    };

    return { session, selected, answers, choose, beginEdit, endEdit, edit, editAddress, check, settle, cancel, reconcile, retrySettlement, phase, notice,
        invalidFields: visibleInvalidFields,
        pending, canEdit, canSubmit, formValid, checksPassed, relevantCheck, accessExpired, awaitingTerminalConfirmation, canRetryTerminalConfirmation,
        canChoose: open && !pending && !busy && allowed('CHOOSE_DOCUMENT'),
        canCheck: open && !busy && phase !== 'check' && Boolean(selected?.checks.length) && allowed('RETRY_CHECK')
            && (!pending || (pending === 'check' && Boolean(checkRequest.current))),
        canDefault: open && !pending && phase === 'idle' && Boolean(session.descriptor.defaults.noDetailsCaseId) && allowed('CONTINUE_WITHOUT_DETAILS'),
        canCancel: open && !busy && phase !== 'check' && allowed('CANCEL'),
        canRetrySettlement: open && !busy && pending === 'settlement' && Boolean(settlementRequest.current),
    };
};
